import { Link, useLocation } from 'react-router-dom'
import { breadcrumbListJsonLd } from '../../../utils/jsonLd'
import JsonLd from '../JsonLd/JsonLd'
import styles from './Breadcrumbs.module.css'

interface BreadcrumbItem {
  path: string
  name: string
  isActive?: boolean
}

interface BreadcrumbsProps {
  customNames?: { [key: string]: string }
  showHome?: boolean
  className?: string
  categoryName?: string // Додаємо назву категорії
  categoryInternalName?: string // Internal name для посилання
}

export default function Breadcrumbs({
  customNames = {},
  showHome = true,
  className = '',
  categoryName, // Додаємо параметр
  categoryInternalName, // Додаємо internal name
}: BreadcrumbsProps) {
  const location = useLocation()
  const pathname = location.pathname
  const pathnames = pathname.split('/').filter(Boolean)

  // Функція для отримання назви хлібної крихти на основі шляху
  const getBreadcrumbName = (path: string, index: number): string => {
    // Спеціальна логіка для товарів - якщо це третій сегмент і перший - shop
    if (pathnames[0] === 'shop' && index === 2 && customNames[path]) {
      return customNames[path] // Назва товару
    }

    // Спеціальна логіка для категорій - якщо це другий сегмент і перший - shop
    if (pathnames[0] === 'shop' && index === 1 && categoryName) {
      return categoryName // Назва категорії
    }

    const breadcrumbNames: { [key: string]: string } = {
      '': 'Головна',
      shop: 'Каталог',
      // Hidden: Sales breadcrumb
      // sales: 'Акції',
      catalog: 'Каталог',
      product: 'Каталог',
      cart: 'Кошик',
      checkout: 'Оформлення замовлення',
      profile: 'Профіль',
      orders: 'Мої замовлення',
      favorites: 'Обране',
      about: 'Про нас',
      contacts: 'Контакти',
      shops: 'Наші магазини',
      vacancies: 'Вакансії',
      philosophy: 'Філософія',
      advantages: 'Переваги',
      home: 'Головна',
      maps: "Кав'ярні",
      ...customNames, // Дозволяємо перевизначити назви через props
    }

    return breadcrumbNames[path] || path.charAt(0).toUpperCase() + path.slice(1)
  }

  // Створюємо масив хлібних крихт
  const breadcrumbs: BreadcrumbItem[] = []

  if (showHome) {
    breadcrumbs.push({ path: '/', name: 'Головна', isActive: pathname === '/' })
  }

  // Спеціальна логіка для сторінок товарів
  if (pathnames[0] === 'product' && pathnames.length === 2) {
    // Для товарів: Головна → Каталог → Категорія → Товар
    breadcrumbs.push({
      path: '/shop',
      name: 'Каталог',
      isActive: false,
    })

    if (categoryName) {
      // Якщо є назва категорії, додаємо її з посиланням якщо є internalName
      breadcrumbs.push({
        path: categoryInternalName ? `/shop/${categoryInternalName}` : '',
        name: categoryName,
        isActive: false,
      })
    }

    // Додаємо товар
    breadcrumbs.push({
      path: pathname,
      name: getBreadcrumbName(pathnames[1], 1),
      isActive: true,
    })
  } else {
    // Стандартна логіка для інших сторінок
    let currentPath = ''
    pathnames.forEach((name, index) => {
      currentPath += `/${name}`
      breadcrumbs.push({
        path: currentPath,
        name: getBreadcrumbName(name, index),
        isActive: index === pathnames.length - 1,
      })
    })
  }

  if (breadcrumbs.length <= 1) {
    return null // Не показуємо хлібні крихти на головній сторінці
  }

  return (
    <nav className={`${styles.breadcrumbs} ${className}`} aria-label="Хлібні крихти">
      <JsonLd data={breadcrumbListJsonLd(breadcrumbs)} />
      <ol className={styles.breadcrumbsList}>
        {breadcrumbs.map((item, index) => (
          <li key={`${item.path}-${index}`} className={styles.breadcrumbItem}>
            {item.isActive ? (
              <span className={styles.breadcrumbCurrent} aria-current="page">
                {item.name}
              </span>
            ) : (
              <>
                {item.path ? (
                  <Link to={item.path} className={styles.breadcrumbLink}>
                    {item.name}
                  </Link>
                ) : (
                  <span className={styles.breadcrumbLink}>{item.name}</span>
                )}
                {index < breadcrumbs.length - 1 && (
                  <span className={styles.breadcrumbSeparator}>/</span>
                )}
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}
